import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { drugsApi } from '../api/drugs.api';
import { CreateDrugDto, UpdateDrugDto } from '../types/drug.types';

export const useDrugs = () => {
  return useQuery({
    queryKey: ['drugs'],
    queryFn: () => drugsApi.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache for 10 minutes
  });
};

export const useDrug = (id: number) => {
  return useQuery({
    queryKey: ['drugs', id],
    queryFn: () => drugsApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateDrug = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDrugDto) => drugsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drugs'] });
      queryClient.invalidateQueries({ queryKey: ['prescription-initial-data'] });
    },
  });
};

export const useUpdateDrug = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDrugDto }) =>
      drugsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['drugs'] });
      queryClient.invalidateQueries({ queryKey: ['drugs', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['prescription-initial-data'] });
    },
  });
};

export const useDeleteDrug = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => drugsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drugs'] });
      queryClient.invalidateQueries({ queryKey: ['prescription-initial-data'] });
    },
  });
};
